import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FlaskConical, Search } from "lucide-react";
import { getIngredients } from "../lib/api";
import SEO, { canonicalFor, titleFor } from "../lib/seo";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");
const ratingLabel = (r) => (r === "red" ? "HIGH RISK" : r === "yellow" ? "MODERATE" : "SAFE");

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    getIngredients().then((d) => setItems(d.ingredients || [])).catch(() => {});
  }, []);

  const filtered = items.filter(
    (i) =>
      i.name.toLowerCase().includes(q.toLowerCase()) ||
      (i.eNumber || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="ingredients-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <SEO
        title={titleFor("Food Additives & Ingredients Encyclopedia")}
        description="Browse plain-English safety breakdowns of common food additives, preservatives, sweeteners and E-numbers — with traffic-light ratings and the products that contain them."
        canonical={canonicalFor("/ingredients")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Food Additives & Ingredients Encyclopedia",
          description: "Safety breakdowns of common food additives and ingredients.",
        }}
      />

      <div className="mb-8">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-2">
          Encyclopedia
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">
          Ingredient <span className="text-[color:var(--lime)]">index</span>
        </h1>
        <p className="mt-3 text-[color:var(--text-2)] max-w-2xl">
          What's really in your food, decoded. Every additive, preservative, sweetener and E-number —
          rated red, amber or green with the science in plain English.
        </p>
      </div>

      <div className="surface rounded-xl px-4 py-2.5 mb-8 flex items-center gap-3 max-w-md">
        <Search className="h-4 w-4 text-[color:var(--text-3)]" />
        <input
          data-testid="ingredients-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search additives or E-numbers..."
          className="bg-transparent outline-none flex-1 text-sm"
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ingredients-grid">
        {filtered.map((ing, i) => {
          const color = ratingColor(ing.rating);
          return (
            <motion.div
              key={ing.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.4) }}
            >
              <Link
                to={`/ingredient/${ing.slug}`}
                data-testid={`ingredient-tile-${ing.slug}`}
                className="surface rounded-xl p-4 block hover:border-[color:var(--lime)]/40 transition-colors h-full"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{ing.name}</div>
                    <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                      {ing.eNumber ? `${ing.eNumber} · ` : ""}
                      {ing.productCount} product{ing.productCount === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span
                    className="font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1 rounded whitespace-nowrap"
                    style={{ color, background: `${color}18`, border: `1px solid ${color}40` }}
                  >
                    {ratingLabel(ing.rating)}
                  </span>
                </div>
                {ing.summary && (
                  <p className="text-xs text-[color:var(--text-2)] mt-3 leading-relaxed line-clamp-3">
                    {ing.summary}
                  </p>
                )}
              </Link>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="surface rounded-xl p-10 text-center text-[color:var(--text-2)]">
          <FlaskConical className="h-8 w-8 mx-auto text-[color:var(--text-3)] mb-3" />
          No ingredients match "{q}".
        </div>
      )}
    </div>
  );
}
